import type { ProblemBody } from "./types";

/** Display a number with thousands separators and at most 3 decimals. */
export function fmt(x: number): string {
  if (Math.abs(x - Math.round(x)) < 1e-9) return Math.round(x).toLocaleString("en-US");
  return x.toLocaleString("en-US", { maximumFractionDigits: 3 });
}

export const r6 = (x: number) => Math.round(x * 1e6) / 1e6;
export const clamp = (x: number, lo = 1, hi = 10) => Math.max(lo, Math.min(hi, x));
export const isqrt = (n: number) => Math.floor(Math.sqrt(n));

export function median(a: number[]): number {
  const s = [...a].sort((x, y) => x - y), m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** 4307 -> [4000, 300, 7] */
export function placeParts(n: number): number[] {
  const s = String(n);
  return [...s].map((c, i) => +c * 10 ** (s.length - i - 1)).filter(Boolean);
}

export const digits = (n: number) => String(Math.floor(Math.abs(n))).length;
/** Count of nonzero digits: zeros cost almost nothing mentally. */
export const nz = (n: number) => [...String(Math.floor(Math.abs(n)))].filter((c) => c !== "0").length;

export function carriesOf(nums: number[]): number {
  let carry = 0, count = 0;
  const w = Math.max(...nums.map(digits));
  for (let i = 0; i < w; i++) {
    const sum = nums.reduce((a, n) => a + (Math.floor(n / 10 ** i) % 10), carry);
    carry = Math.floor(sum / 10);
    if (carry) count++;
  }
  return count;
}

export function borrowsOf(a: number, b: number): number {
  let borrow = 0, count = 0;
  for (let i = 0; i < digits(a); i++) {
    const da = (Math.floor(a / 10 ** i) % 10) - borrow, db = Math.floor(b / 10 ** i) % 10;
    borrow = da < db ? 1 : 0;
    count += borrow;
  }
  return count;
}

export function prob(
  q: string, answer: number, hint: string,
  o: { note?: string; tol?: number; rel?: boolean; t: number },
): ProblemBody {
  return { q, answer, hint, note: o.note ?? "", tol: o.tol ?? 0, rel: !!o.rel, t: Math.round(o.t * 2) / 2 };
}
